import { VirtualTrade } from "../types";

const STORAGE_KEY = 'tradevision_replay_session_v1';

export interface ReplayTradingSession {
  id: string;
  ticker: string;
  timeframe: string;
  startTime: number;
  trades: VirtualTrade[];
  balance: number;
  initialBalance: number;
  timestamp: number;
  // Replay state for restoration
  replayState?: {
    currentIndex: number;
    tickIndex: number;
    replayTime: number;
    displayTime: number;
    isPlaying: boolean;
    speed: number;
  };
  // Chart data at the time of save
  currentCandles?: any[];
  fullHistory?: any[];
  // Tick buffer for replay continuation
  tickBuffer?: any[];
}

// Synchronous check if a replay session exists (for initial render optimization)
export const hasReplaySession = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const session = JSON.parse(raw);
    // Check if it has the required fields for a restorable replay session
    return !!(session && session.replayState && session.currentCandles && session.fullHistory);
  } catch {
    return false;
  }
};

// Get current replay session (only one active at a time), localStorage-only
export const getReplaySession = async (): Promise<ReplayTradingSession | null> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error("Failed to load replay session", e);
    return null;
  }
};

// Save/update replay session (localStorage-only)
export const saveReplaySession = async (
  session: ReplayTradingSession
): Promise<void> => {
  try {
    // Local Storage Save (without tick buffer to avoid quota issues)
    try {
      const lightSession = {
        ...session,
        tickBuffer: undefined // Don't save tick buffer to localStorage (too large)
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lightSession));
    } catch (storageError: any) {
      if (storageError.name === 'QuotaExceededError') {
        console.warn('[ReplaySession] localStorage quota exceeded, saving without tick buffer');
        // Try again with minimal data
        const minimalSession = {
          id: session.id,
          ticker: session.ticker,
          timeframe: session.timeframe,
          trades: session.trades,
          balance: session.balance,
          initialBalance: session.initialBalance,
          timestamp: session.timestamp,
          replayState: session.replayState
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(minimalSession));
      } else {
        throw storageError;
      }
    }
  } catch (e) {
    console.error("Failed to save replay session", e);
  }
};

// Clear replay session (when exiting replay)
export const clearReplaySession = async (): Promise<void> => {
  try {
    console.log('[ReplaySession] Clearing localStorage');
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error("Failed to clear replay session", e);
  }
};
